import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABELS = {
  pending: "în așteptare",
  confirmed: "confirmat",
  unsubscribed: "dezabonat",
};

export function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const loadLeads = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads${status ? `?status=${status}` : ""}`);
      if (res.status === 401) {
        setAuthed(false);
        setError("Autentifică-te din nou.");
        return;
      }
      if (!res.ok) throw new Error("Eroare la încărcarea lead-urilor.");
      setLeads(await res.json());
      setAuthed(true);
      setError("");
    } catch (e) {
      setError(e.message || "Eroare la încărcarea lead-urilor.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeads("");
  }, [loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Eroare la autentificare.");
        return;
      }
      setPassword("");
      loadLeads("");
    } catch {
      setError("Eroare la autentificare.");
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/admin/logout`, { method: "POST" });
    } catch {
      /* sesiunea expiră oricum */
    }
    setAuthed(false);
    setLeads([]);
  };

  const removeLead = async (id) => {
    await fetch(`${API}/api/admin/leads/${id}`, { method: "DELETE" });
    loadLeads(filter);
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/api/admin/leads/export`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mindregen-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-base text-ink">
        <main className="mx-auto w-full max-w-[520px] px-6 pt-24 pb-16">
          <h1 className="font-display text-4xl font-bold">Admin</h1>
          <form onSubmit={login} className="mt-8 rounded-[28px] border border-teal/25 bg-surface p-8">
            <label htmlFor="admin-password" className="block text-sm font-medium">
              Parolă admin
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 min-h-[52px] w-full rounded-xl border border-teal/25 bg-raised px-4 text-base text-ink"
            />
            <p aria-live="assertive" role="alert" data-testid="admin-error" className="empty:hidden mt-4 text-sm font-medium text-[#FF8A7A]">
              {error}
            </p>
            <button
              type="submit"
              data-testid="admin-login-button"
              className="mt-6 w-full min-h-[56px] rounded-full bg-teal font-display text-[15px] font-bold uppercase tracking-wide text-base transition-colors duration-200 hover:bg-teal-dark"
            >
              Intră în panou
            </button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base text-ink">
      <main className="mx-auto w-full max-w-[860px] px-6 pt-16 pb-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-bold" data-testid="admin-panel-title">
            Lead-uri
          </h1>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="inline-flex min-h-[52px] items-center rounded-full border border-teal/25 bg-surface px-6 text-sm font-medium transition-colors duration-200 hover:border-teal"
            >
              Export CSV
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex min-h-[52px] items-center rounded-full px-4 text-sm text-teal transition-colors duration-200 hover:text-ink"
            >
              Ieși
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <label htmlFor="admin-filter" className="text-sm font-medium">
            Filtru:
          </label>
          <select
            id="admin-filter"
            data-testid="admin-filter"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              loadLeads(e.target.value);
            }}
            className="min-h-[52px] rounded-xl border border-teal/25 bg-surface px-4 text-base text-ink"
          >
            <option value="">Toate</option>
            <option value="pending">în așteptare</option>
            <option value="confirmed">confirmat</option>
            <option value="unsubscribed">dezabonat</option>
          </select>
          {loading && <p className="text-sm text-soft">Se încarcă…</p>}
        </div>

        <p aria-live="polite" role="alert" data-testid="admin-error" className="empty:hidden mt-4 text-sm font-medium text-[#FF8A7A]">
          {error}
        </p>

        <div className="mt-6 overflow-x-auto rounded-[20px] border border-teal/25 bg-surface">
          <table data-testid="admin-leads-table" className="w-full min-w-[880px] whitespace-nowrap text-left text-sm">
            <thead>
              <tr className="border-b border-teal/25 text-[11px] uppercase tracking-wider text-soft">
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Profil</th>
                <th className="px-4 py-3 font-medium">Intensitate</th>
                <th className="px-4 py-3 font-medium">Screen time</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Acord</th>
                <th className="px-4 py-3 font-medium">Vers.</th>
                <th className="sr-only">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-soft" data-testid="admin-empty">
                    Niciun lead încă.
                  </td>
                </tr>
              )}
              {leads.map((lead) => (
                <tr key={lead.id} className="border-b border-teal/10 last:border-0">
                  <td className="px-4 py-3" data-testid="admin-lead-email">{lead.email}</td>
                  <td className="px-4 py-3">{lead.profile}</td>
                  <td className="px-4 py-3">{lead.intensity}</td>
                  <td className="px-4 py-3">{lead.screen_time_range}</td>
                  <td className="px-4 py-3">{STATUS_LABELS[lead.status] || lead.status}</td>
                  <td className="px-4 py-3">{formatDate(lead.consent_at)}</td>
                  <td className="px-4 py-3">{lead.consent_version}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      data-testid={`admin-delete-button-${lead.id}`}
                      onClick={() => removeLead(lead.id)}
                      className="min-h-[44px] rounded-full px-3 text-[13px] font-medium text-[#FF8A7A] transition-colors duration-200 hover:bg-[#FF8A7A]/10"
                    >
                      Șterge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
